import { useEffect, useState } from 'react';
import { Alert, Button, Divider, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAt, IconBriefcase, IconDeviceFloppy, IconHash, IconId, IconMapPin, IconPhone, IconUser, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import api from '../../api/axiosConfig';

export default function AddEmployeeModal({ opened, onClose, onSave, editData = null }) {
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [error, setError] = useState('');
  const form = useForm({
    initialValues: { identity_number: '', employee_no: '', first_name: '', last_name: '', phone: '', email: '', employee_type: 'OFFICE', location: 'Merkez', department: '', job_title_id: null, hire_date: '' },
    validate: {
      identity_number: (value) => value && (value.length !== 11 || !/^\d+$/.test(value)) ? 'T.C. Kimlik No 11 haneli olmalıdır' : null,
      first_name: (value) => value.trim().length < 2 ? 'Ad en az 2 karakter olmalıdır' : null,
      last_name: (value) => value.trim().length < 2 ? 'Soyad en az 2 karakter olmalıdır' : null,
      location: (value) => !value ? 'Lokasyon seçin' : null,
      job_title_id: (value) => !value ? 'Görev / unvan seçin' : null,
    },
  });

  useEffect(() => {
    if (!opened) return;
    Promise.all([api.get('/hr/definitions/locations'), api.get('/hr/definitions/departments')])
      .then(([locationRes, departmentRes]) => { setLocations(locationRes.data); setDepartments(departmentRes.data); })
      .catch((err) => setError(err.response?.data?.detail || 'Tanımlar alınamadı.'));
  }, [opened]);

  useEffect(() => {
    if (!opened) return;
    api.get('/hr/job-titles', { params: { employee_type: form.values.employee_type } })
      .then((response) => setJobTitles(response.data))
      .catch((err) => setError(err.response?.data?.detail || 'Unvanlar alınamadı.'));
  }, [opened, form.values.employee_type]);

  useEffect(() => {
    if (!opened) return;
    if (editData) {
      const type = editData.employee_type || (editData.location && editData.location !== 'Merkez' ? 'SITE' : 'OFFICE');
      form.setValues({ identity_number: editData.identity_number || editData.tc || '', employee_no: editData.employee_no || '', first_name: editData.first_name || editData.name?.split(' ')[0] || '', last_name: editData.last_name || editData.name?.split(' ').slice(1).join(' ') || '', phone: editData.phone === '-' ? '' : (editData.phone || ''), email: editData.email || '', employee_type: type, location: type === 'SITE' ? (editData.site_name || editData.location || '') : 'Merkez', department: editData.department === '-' ? '' : (editData.department || ''), job_title_id: editData.job_title_id ? String(editData.job_title_id) : null, hire_date: editData.hire_date ? dayjs(editData.hire_date).format('YYYY-MM-DD') : '' });
    } else {
      form.reset(); form.setValues({ employee_type: 'OFFICE', location: 'Merkez' });
    }
    setError('');
  }, [opened, editData?.id]);

const handleSubmit = async (values) => {
  const payload = {
    identity_number: values.identity_number.trim() || null,
    employee_no: values.employee_no.trim() || null,

    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),

    phone: values.phone.trim() || null,
    email: values.email.trim() || null,

    employee_type: values.employee_type,
    job_title_id: Number(values.job_title_id),

    // Hem Merkez Ofis hem Şantiye personelinde departman kaydedilir
    department: values.department || null,

    // Sadece şantiye personelinde site_name bulunur
    site_name:
      values.employee_type === 'SITE'
        ? values.location
        : null,

    hire_date: values.hire_date || null,

    is_active: editData?.is_active ?? true,
  };

  try {
    await onSave?.(payload);
  } catch (err) {
    setError(
      err.response?.data?.detail ||
      'Personel kaydedilemedi.'
    );
    return;
  }
};
  const handleClose = () => { form.reset(); setError(''); onClose(); };
  const locationOptions = locations.map((item) => ({ value: item.name, label: item.name }));

  return <Modal opened={opened} onClose={handleClose} title={<Text fw={800} size="lg">{editData ? 'Personel Düzenle' : 'Yeni Personel Ekle'}</Text>} size="xl" radius="md" closeButtonProps={{ icon: <IconX size={20} /> }}>
    <form onSubmit={form.onSubmit(handleSubmit)}><Stack gap="lg">{error && <Alert color="red">{String(error)}</Alert>}
      <div><Divider mb="sm" label={<Text fw={600} c="blue.6">Kimlik Bilgileri</Text>} /><SimpleGrid cols={{ base: 1, sm: 2 }}><TextInput label="T.C. Kimlik Numarası" maxLength={11} leftSection={<IconId size={18} />} {...form.getInputProps('identity_number')} /><TextInput label="Sicil No" leftSection={<IconHash size={18} />} {...form.getInputProps('employee_no')} /><TextInput withAsterisk label="Ad" leftSection={<IconUser size={18} />} {...form.getInputProps('first_name')} /><TextInput withAsterisk label="Soyad" {...form.getInputProps('last_name')} /></SimpleGrid></div>
      <div><Divider mb="sm" label={<Text fw={600} c="blue.6">İletişim Bilgileri</Text>} /><SimpleGrid cols={{ base: 1, sm: 2 }}><TextInput label="Cep Telefonu" leftSection={<IconPhone size={18} />} {...form.getInputProps('phone')} /><TextInput label="E-posta" leftSection={<IconAt size={18} />} {...form.getInputProps('email')} /></SimpleGrid></div>
      <div><Divider mb="sm" label={<Text fw={600} c="blue.6">Kurumsal Bilgiler</Text>} /><SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select withAsterisk label="Personel Tipi" data={[{ value: 'OFFICE', label: 'Merkez Ofis' }, { value: 'SITE', label: 'Şantiye' }]} {...form.getInputProps('employee_type')} onChange={(value) => { form.setFieldValue('employee_type', value); form.setFieldValue('location', value === 'OFFICE' ? 'Merkez' : ''); form.setFieldValue('job_title_id', null); }} />
        <Select withAsterisk searchable label="Lokasyon" leftSection={<IconMapPin size={18} />} data={locationOptions} {...form.getInputProps('location')} disabled={form.values.employee_type === 'OFFICE'} />
        <Select searchable clearable label="Departman" data={departments.map((item) => ({ value: item.name, label: item.name }))} {...form.getInputProps('department')} />
        <Select withAsterisk searchable label="Görev / Unvan" leftSection={<IconBriefcase size={18} />} data={jobTitles.map((item) => ({ value: String(item.id), label: item.name }))} {...form.getInputProps('job_title_id')} />
        <TextInput label="İşe Giriş Tarihi" type="date" {...form.getInputProps('hire_date')} />
      </SimpleGrid></div>
    </Stack><Group justify="flex-end" mt="xl" pt="md" style={{ borderTop: '1px solid #e2e8f0' }}><Button variant="default" onClick={handleClose}>İptal</Button><Button type="submit" leftSection={<IconDeviceFloppy size={18} />}>{editData ? 'Değişiklikleri Kaydet' : 'Kaydet ve Oluştur'}</Button></Group></form>
  </Modal>;
}
